import React from "react";
import { motion } from "framer-motion";
import {
  TreePine,
  Factory,
  Truck,
  ClipboardCheck,
  ShieldCheck,
  Globe
} from "lucide-react";
import Navbar from "../../components/Navbar";

const fscServices = [
  {
    title: "FSC Chain of Custody (CoC)",
    description:
      "Ensures full traceability of FSC-certified materials throughout the supply chain, from forest to final product.",
    icon: TreePine
  },
  {
    title: "FSC Controlled Wood",
    description:
      "Helps organizations avoid unacceptable sources of wood and comply with FSC Controlled Wood requirements.",
    icon: ShieldCheck
  },
  {
    title: "Manufacturing & Processing Units",
    description:
      "Certification support for manufacturers and processors handling FSC-certified raw materials and products.",
    icon: Factory
  },
  {
    title: "Trading & Distribution",
    description:
      "Guidance for traders, wholesalers, and distributors to maintain FSC compliance in purchasing and sales.",
    icon: Truck
  },
  {
    title: "Documentation & Traceability",
    description:
      "Development of FSC procedures, records, and documentation ensuring audit readiness and transparency.",
    icon: ClipboardCheck
  },
  {
    title: "Global Market Access",
    description:
      "FSC certification enhances credibility, sustainability commitment, and access to international markets.",
    icon: Globe
  }
];

const FSC_COC_Certifications = () => {
  return (
    <>
    <Navbar />
    <section className="bg-gray-50 py-16 sm:py-14 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">

        {/* ================= Header ================= */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="max-w-3xl mx-auto text-center mb-14"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-[var(--bg-primary)] mb-4">
            FSC Chain of Custody (CoC) Certifications
          </h2>
          <div className="w-20 h-1 bg-[var(--bg-primary)] mx-auto rounded-full mb-4"></div>
          <p className="text-gray-600 text-base md:text-lg">
            MBA Solutions provides professional consultancy services to help
            organizations achieve FSC Chain of Custody certification, ensuring
            responsible sourcing, traceability, and sustainability across the
            supply chain.
          </p>
        </motion.div>

        {/* ================= Cards ================= */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {fscServices.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                whileHover={{
                  y: -10,
                  scale: 1.04,
                  boxShadow: "0px 25px 50px rgba(34,197,94,0.25)"
                }}
                className="relative bg-white rounded-2xl p-7 border border-gray-100 shadow-lg group overflow-hidden"
              >
                {/* Glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 via-transparent to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl" />

                {/* Icon */}
                <div className="relative z-10 w-14 h-14 flex items-center justify-center rounded-xl bg-[var(--bg-primary)] text-white mb-5 shadow-md">
                  <Icon size={28} />
                </div>

                {/* Title */}
                <h3 className="relative z-10 text-lg font-bold text-gray-900 mb-2 group-hover:text-[var(--bg-primary)] transition-colors duration-300">
                  {item.title}
                </h3>

                {/* Description */}
                <p className="relative z-10 text-sm text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                  {item.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* ================= CTA ================= */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mt-16 text-center"
        >
          <p className="text-gray-600 mb-6">
            Strengthen your sustainability credentials with FSC CoC
            certification.
          </p>
          <button className="inline-flex items-center justify-center px-8 py-3 rounded-full bg-[var(--bg-primary)] text-white font-semibold shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300">
            Get FSC CoC Consultation
          </button>
        </motion.div>

      </div>
    </section>
    </>
  );
};

export default FSC_COC_Certifications;
